import { Component, HostListener, effect, inject, signal } from "@angular/core";
import { CommonModule } from "@angular/common";
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from "@angular/router";
import { filter } from "rxjs/operators";
import { ApiService } from "./core/api.service";
import { AuthService } from "./core/auth.service";
import { FirebaseRealtimeService } from "./core/firebase-realtime.service";
import { SafeHtmlPipe } from "./core/safe-html.pipe";
import { initials } from "./core/utils";
import { PaywallComponent } from "./pages/paywall.component";
import { SuspendedComponent } from "./pages/suspended.component";
import { AnnouncementBannerComponent } from "./core/announcement-banner.component";
import { TourOverlayComponent } from "./core/tour-overlay.component";
import { TourService } from "./core/tour.service";
import { LimitDialogComponent } from "./shared/limit-dialog.component";
import { ToastContainerComponent } from "./shared/toast-container.component";

interface NavLink {
  path: string;
  label: string;
  icon: string;
  can: () => boolean;
  badge?: () => number;
}

@Component({
  selector: "app-root",
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    SafeHtmlPipe,
    PaywallComponent,
    SuspendedComponent,
    AnnouncementBannerComponent,
    TourOverlayComponent,
    LimitDialogComponent,
    ToastContainerComponent,
  ],
  template: `
    <app-toast-container></app-toast-container>
    <app-suspended *ngIf="auth.suspended(); else paywallOrShell"></app-suspended>

    <ng-template #paywallOrShell>
    <app-paywall *ngIf="auth.locked(); else shellOrBare"></app-paywall>

    <ng-template #shellOrBare>
    <div class="app-shell" [class.nav-collapsed]="collapsed()" *ngIf="auth.user() && !auth.isClient(); else bare">
      <header class="mobile-header">
        <button class="hamburger" type="button" (click)="toggleMenu()" aria-label="Toggle menu">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
        </button>
        <div class="mobile-brand">
          <span class="mobile-brand-mark">
            <img src="talos-foreman.png" alt="Talos Foreman" />
          </span>
          <span class="mobile-brand-name">Talos Foreman</span>
        </div>
      </header>

      <div class="sidebar-backdrop" [class.show]="menuOpen()" (click)="closeMenu()"></div>

      <aside class="sidebar" [class.open]="menuOpen()">
        <div class="brand">
          <div class="brand-mark">
            <img src="talos-foreman.png" alt="Talos Foreman" />
          </div>
          <div class="brand-text">
            <div class="brand-name">Talos</div>
            <div class="brand-sub">Foreman</div>
          </div>
        </div>

        <button
          class="sidebar-collapse-btn"
          type="button"
          (click)="toggleCollapsed()"
          [title]="collapsed() ? 'Expand menu' : 'Collapse menu'"
          [attr.aria-label]="collapsed() ? 'Expand menu' : 'Collapse menu'"
          [attr.aria-expanded]="!collapsed()"
        >
          <svg *ngIf="!collapsed()" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          <svg *ngIf="collapsed()" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
        </button>

        <nav>
          <div class="nav-group-label">Overview</div>
          <a *ngFor="let l of visible(overview)" class="nav-item" [routerLink]="l.path" routerLinkActive="active" [title]="collapsed() ? l.label : ''" [attr.data-tour]="l.path" (click)="closeMenu()">
            <span [innerHTML]="l.icon | safeHtml"></span>
            <span class="nav-label">{{ l.label }}</span>
            <span class="nav-badge" *ngIf="l.badge && l.badge()! > 0">{{ l.badge() }}</span>
          </a>

          <ng-container *ngIf="visible(operations).length">
            <div class="nav-group-label">Operations</div>
            <a *ngFor="let l of visible(operations)" class="nav-item" [routerLink]="l.path" routerLinkActive="active" [title]="collapsed() ? l.label : ''" [attr.data-tour]="l.path" (click)="closeMenu()">
              <span [innerHTML]="l.icon | safeHtml"></span>
              <span class="nav-label">{{ l.label }}</span>
              <span class="nav-badge" *ngIf="l.badge && l.badge()! > 0">{{ l.badge() }}</span>
            </a>
          </ng-container>

          <ng-container *ngIf="visible(marketing).length">
            <div class="nav-group-label">Marketing Hub</div>
            <a *ngFor="let l of visible(marketing)" class="nav-item" [routerLink]="l.path" routerLinkActive="active" [title]="collapsed() ? l.label : ''" [attr.data-tour]="l.path" (click)="closeMenu()">
              <span [innerHTML]="l.icon | safeHtml"></span>
              <span class="nav-label">{{ l.label }}</span>
              <span class="nav-badge" *ngIf="l.badge && l.badge()! > 0">{{ l.badge() }}</span>
            </a>
          </ng-container>

          <ng-container *ngIf="visible(admin).length">
            <div class="nav-group-label">Administration</div>
            <a *ngFor="let l of visible(admin)" class="nav-item" [routerLink]="l.path" routerLinkActive="active" [title]="collapsed() ? l.label : ''" [attr.data-tour]="l.path" (click)="closeMenu()">
              <span [innerHTML]="l.icon | safeHtml"></span>
              <span class="nav-label">{{ l.label }}</span>
            </a>
          </ng-container>
        </nav>

        <button class="tour-launch" type="button" *ngIf="auth.isAdmin()" (click)="startTour()">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
          <span class="nav-label">Take a tour</span>
        </button>

        <div class="sidebar-footer">
          <div class="avatar">{{ ini(auth.user()!.name) }}</div>
          <div class="sidebar-footer-info" style="min-width:0;flex:1">
            <div style="font-weight:600;font-size:13px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">{{ auth.user()!.name }}</div>
            <div style="font-size:11.5px;color:var(--text-3)">{{ auth.user()!.role_name || "Employee" }}</div>
          </div>
          <button class="icon-btn" title="Sign out" (click)="logout()">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
          </button>
        </div>
      </aside>

      <div class="main">
        <div class="trial-banner" *ngIf="auth.trialDaysLeft() !== null">
          <span>
            <strong>{{ auth.trialDaysLeft() }}</strong>
            {{ auth.trialDaysLeft() === 1 ? "day" : "days" }} left in your free trial.
          </span>
          <a routerLink="/billing" [queryParams]="{ tab: 'plans' }" *ngIf="auth.isAdmin()" class="trial-cta"
            >Subscribe now</a
          >
        </div>
        <div class="trial-banner" *ngIf="tokensBanner()" style="background:#dcfce7;color:#166534">
          <span><strong>Payment received.</strong> Your token bundle is being applied — credits appear in a moment.</span>
          <a href="javascript:void(0)" class="trial-cta" (click)="tokensBanner.set(false)">Dismiss</a>
        </div>
        <div class="trial-banner" *ngIf="usageWarning() as w" style="background:#fef3c7;color:#92400e">
          <span>
            <strong>Heads up:</strong> you've used {{ w.used }} of {{ w.limit }} {{ w.label }}
            ({{ w.pct }}% of your plan).
          </span>
          <span style="display:flex;gap:10px;align-items:center">
            <a routerLink="/billing" [queryParams]="{ tab: 'bundles' }" class="trial-cta">Buy tokens</a>
            <a href="javascript:void(0)" class="trial-cta" style="opacity:.7" (click)="dismissUsageWarning()">Dismiss</a>
          </span>
        </div>
        <app-announcement-banner audience="staff"></app-announcement-banner>
        <router-outlet></router-outlet>
      </div>
    </div>

    <app-tour-overlay></app-tour-overlay>
    <app-limit-dialog></app-limit-dialog>

    <ng-template #bare>
      <router-outlet></router-outlet>
    </ng-template>
    </ng-template>
    </ng-template>
  `,
})
export class AppComponent {
  private api = inject(ApiService);
  auth = inject(AuthService);
  private router = inject(Router);
  private tour = inject(TourService);
  private realtime = inject(FirebaseRealtimeService);
  unread = signal(0);
  overdue = signal(0);
  teamUnread = signal(0);
  menuOpen = signal(false);
  collapsed = signal(localStorage.getItem("sidebar_collapsed") === "1");
  ini = initials;
  tokensBanner = signal(false);
  usageWarning = signal<{
    label: string;
    used: number;
    limit: number;
    pct: number;
  } | null>(null);
  private usageWarningDismissed =
    sessionStorage.getItem("usage_warning_dismissed") === "1";

  dismissUsageWarning() {
    this.usageWarning.set(null);
    this.usageWarningDismissed = true;
    sessionStorage.setItem("usage_warning_dismissed", "1");
  }

  toggleMenu() {
    this.menuOpen.update((v) => !v);
  }

  toggleCollapsed() {
    this.collapsed.update((v) => !v);
    localStorage.setItem("sidebar_collapsed", this.collapsed() ? "1" : "0");
  }

  closeMenu() {
    this.menuOpen.set(false);
  }

  @HostListener("document:keydown.escape")
  onEscape() {
    if (this.menuOpen()) this.closeMenu();
  }

  private svg(path: string): string {
    return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${path}</svg>`;
  }

  overview: NavLink[] = [
    { path: "/dashboard", label: "Dashboard", icon: this.svg('<rect x="3" y="3" width="7" height="9"/><rect x="14" y="3" width="7" height="5"/><rect x="14" y="12" width="7" height="9"/><rect x="3" y="16" width="7" height="5"/>'), can: () => this.auth.isOffice() },
    { path: "/my", label: "My Work", icon: this.svg('<path d="M20 6 9 17l-5-5"/>'), can: () => true },
    { path: "/team", label: "Team Chat", icon: this.svg('<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>'), can: () => true, badge: () => this.teamUnread() },
  ];

  operations: NavLink[] = [
    { path: "/projects", label: "Projects", icon: this.svg('<path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>'), can: () => this.auth.has("projects.manage") },
    { path: "/clients", label: "Clients", icon: this.svg('<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>'), can: () => this.auth.has("clients.manage") },
    { path: "/subcontractors", label: "Subcontractors", icon: this.svg('<path d="M2 20h20"/><path d="M12 4v16"/><path d="M6 8l6-4 6 4"/><path d="M6 8v12"/><path d="M18 8v12"/>'), can: () => this.auth.has("subcontractors.manage") },
    { path: "/proposals", label: "Site Proposals", icon: this.svg('<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><path d="M9 13h6"/><path d="M9 17h4"/>'), can: () => this.auth.has("proposals.manage") },
    { path: "/scheduling", label: "Scheduling", icon: this.svg('<rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>'), can: () => this.auth.has("scheduling.manage") },
  ];

  marketing: NavLink[] = [
    { path: "/crm", label: "CRM", icon: this.svg('<path d="M3 4h18l-7 8v6l-4 2v-8z"/>'), can: () => this.auth.has("marketing.manage") },
    { path: "/website", label: "Website", icon: this.svg('<circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>'), can: () => this.auth.has("marketing.manage") },
    { path: "/marketing", label: "Marketing", icon: this.svg('<path d="M3 11l18-5v12L3 13z"/><path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"/>'), can: () => this.auth.has("marketing.manage") },
  ];

  admin: NavLink[] = [
    { path: "/employees", label: "Employees", icon: this.svg('<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/>'), can: () => this.auth.has("employees.manage") },
    { path: "/timesheets", label: "Timesheets & Crew Map", icon: this.svg('<circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>'), can: () => this.auth.has("employees.manage") },
    { path: "/roles", label: "Roles & Access", icon: this.svg('<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>'), can: () => this.auth.has("roles.manage") },
    { path: "/estimating", label: "Estimating & Costs", icon: this.svg('<line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>'), can: () => this.auth.isAdmin() },
    { path: "/settings", label: "Company Settings", icon: this.svg('<circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>'), can: () => this.auth.isAdmin() },
    { path: "/landing-content", label: "Landing Content", icon: this.svg('<path d="M3 3h18v4H3z"/><path d="M3 11h7v10H3z"/><path d="M14 11h7v10h-7z"/>'), can: () => this.auth.isAdmin() },
    { path: "/billing", label: "Billing", icon: this.svg('<rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/>'), can: () => this.auth.isAdmin() },
  ];

  constructor() {
    this.tour.registerMenuControl((open) => this.menuOpen.set(open));

    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe(() => {
        // Keep the drawer open while the tour is spotlighting nav items.
        if (!this.tour.active()) this.closeMenu();
      });

    // Auto-start the guided tour for admins who haven't completed it yet
    // (fires once per session, after login/refresh resolves the user, and
    // only when the full office shell is actually rendered).
    effect(() => {
      const u = this.auth.user();
      if (u && this.auth.isAdmin() && !this.auth.suspended() && !this.auth.locked()) {
        setTimeout(() => this.tour.maybeAutoStart());
      }
    });

    effect(() => {
      document.body.classList.toggle("menu-open", this.menuOpen());
    });

    // The guided tour spotlights nav labels — expand the sidebar while it runs.
    effect(() => {
      if (this.tour.active() && this.collapsed()) this.collapsed.set(false);
    });

    effect(() => {
      if (this.auth.isOffice()) {
        this.api.getDashboard().subscribe({
          next: (d) => {
            this.unread.set(d.unread_messages);
            this.overdue.set(d.overdue_tasks);
          },
          error: () => {},
        });
      }
    });

    // Post-checkout landing: Stripe bounces the buyer back to the page they
    // were working on with ?tokens=success — show a confirmation banner.
    if (new URLSearchParams(window.location.search).get("tokens") === "success") {
      this.tokensBanner.set(true);
    }

    // Proactive usage nudge: warn admins when any plan resource crosses 80%.
    effect(() => {
      const u = this.auth.user();
      if (!u || !this.auth.isAdmin() || this.usageWarningDismissed) return;
      this.api.getBilling().subscribe({
        next: (s) => {
          const labels: Record<string, string> = {
            projects: "projects",
            clients: "clients",
            employees: "team members",
            subcontractors: "subcontractors",
          };
          let worst: { label: string; used: number; limit: number; pct: number } | null =
            null;
          for (const [key, r] of Object.entries(s.usage?.resources || {})) {
            if (r.limit === null || r.limit === 0) continue;
            const pct = Math.floor((r.used / r.limit) * 100);
            if (pct >= 80 && pct < 100 && (!worst || pct > worst.pct)) {
              worst = { label: labels[key] || key, used: r.used, limit: r.limit, pct };
            }
          }
          this.usageWarning.set(worst);
        },
        error: () => {},
      });
    });

    effect((onCleanup) => {
      const u = this.auth.user();
      const companyId = u?.company_id;
      if (!u || this.auth.isClient() || !companyId) return;

      // Initial value from REST; live updates then take over via the RTDB
      // per-employee unread node so the sidebar badge doesn't need polling.
      this.api.getTeamUnreadCount().subscribe({
        next: (res) => this.teamUnread.set(res.unread_count),
        error: () => {},
      });

      const stop = this.realtime.listenToUnreadCount(companyId, u.id, (count) => this.teamUnread.set(count));
      onCleanup(stop);
    });
  }

  visible(links: NavLink[]): NavLink[] {
    return links.filter((l) => l.can());
  }

  startTour() {
    this.closeMenu();
    this.tour.start();
  }

  logout() {
    this.auth.logout().subscribe({
      next: () => this.router.navigate(["/login"]),
      error: () => this.router.navigate(["/login"]),
    });
  }
}
